import { useEffect, useState } from 'react';
import EmailSection from '@sections/signup/email';
import PhoneSection from '@sections/signup/phone';
import useSignupContext from '@hooks/context/useSignupContext';
import VerificationPhoneSection from '@sections/signup/verification_phone';
import PasswordSection from '@sections/signup/password';
import PersonalDetailsSection from '@sections/signup/personal_details';
import BillingSection from '@sections/signup/billing';
import CongratsSection from '@sections/signup/congrats';
import AccountTypeSection from '@sections/signup/account_type';
import styles from './styles.module.scss';
import { useForm } from '@hooks/helpers/useForm';
import { FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    signupBusinessStepsValidation,
    signupPersonalStepsValidation
} from '@helpers/validation_objects/signup';
import {
    addressValidationErrors,
    defaultBusinessSignUpData,
    defaultPersonalSignUpData
} from '@const/default_form_data';
import { EAddressType, EUserType } from '@xeppt/xeppt-sdk/types';
import BusinessAddressSection from '@sections/signup/business_address';
import BusinessDetailsSection from '@sections/signup/business_details';
import { useLocales } from '@hooks/helpers/useLocales';
import { motion } from 'framer-motion';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import { slideLeftToRightAnimation, slideRightToLeftAnimation } from '@const/animation';

const SignUpLayout = () => {
    const { validationLocale } = useLocales();
    const [isPhoneVerification, setIsPhoneVerification] = useState(false);
    const [addressStep, setAddressStep] = useState(EAddressType.BUSINESS);
    const {
        currentStep,
        availableStep,
        type,
        onChangeType,
        animation,
        changeStep,
        handleChangeAnimation
    } = useSignupContext();
    const [savedStep, setSavedStep] = useState<string | undefined>('1');
    const [emailSent, setEmailSent] = useState(false);
    const [isRender, setIsRender] = useState(false);

    const formMethods = useForm({
        defaultValues: {
            ...(type === EUserType.INDIVIDUAL
                ? defaultPersonalSignUpData
                : defaultBusinessSignUpData)
        },
        resolver: zodResolver(
            type === EUserType.INDIVIDUAL
                ? signupPersonalStepsValidation({
                      currentStep: Number(currentStep?.id || 1) - 1,
                      phoneVerification: isPhoneVerification,
                      isEmailSent: emailSent,
                      messages: {
                          email: validationLocale('email'),
                          phone: validationLocale('phone'),
                          phoneVerifyCode: validationLocale('phoneVerifyCode'),
                          first_name: validationLocale('first_name'),
                          last_name: validationLocale('last_name'),
                          birth_date: validationLocale('birth_date'),
                          tag: validationLocale('tag'),
                          occupancy: validationLocale('occupation'),
                          password: validationLocale('password'),
                          password_confirm: validationLocale('password_confirm'),
                          billing_address: addressValidationErrors(validationLocale)
                      }
                  })
                : signupBusinessStepsValidation({
                      addressType: addressStep,
                      currentStep: Number(currentStep?.id || 1) - 1,
                      phoneVerification: isPhoneVerification,
                      isEmailSent: emailSent,
                      messages: {
                          email: validationLocale('email'),
                          phone: validationLocale('phone'),
                          phoneVerifyCode: validationLocale('phoneVerifyCode'),
                          first_name: validationLocale('first_name'),
                          last_name: validationLocale('last_name'),
                          birth_date: validationLocale('birth_date'),
                          address: addressValidationErrors(validationLocale),
                          password: validationLocale('password'),
                          password_confirm: validationLocale('password_confirm'),
                          residence: validationLocale('residence'),
                          tag: validationLocale('tag'),
                          occupancy: validationLocale('occupation'),
                          business: {
                              company_name: validationLocale('company_name'),
                              tradingName: validationLocale('tradingName'),
                              registration_number: validationLocale('registration_number'),
                              category: validationLocale('category'),
                              size: validationLocale('size'),
                              website: validationLocale('website')
                          }
                      }
                  })
        )
    });

    const [token] = useQueryParams(['token']);

    useEffect(() => {
        setIsRender(false);
        setTimeout(() => setIsRender(true), 0);
    }, [animation, isPhoneVerification, emailSent]);

    useEffect(() => {
        changeStep('1');
        if (token) {
            changeStep('7');
            formMethods.setValue('code', token);
        }
    }, []);

    useEffect(() => {
        if (availableStep?.id === savedStep) {
            setTimeout(() => {
                formMethods.trigger();
            }, 100);
        } else {
            setSavedStep(currentStep?.id);
        }
    }, [currentStep, availableStep]);

    const renderPersonalSections = () => {
        switch (currentStep?.id) {
            case '1':
                return <AccountTypeSection value={type} onChange={(val) => onChangeType(val)} />;
            case '2':
                return (
                    <EmailSection
                        isEmailSent={emailSent}
                        toggleIsEmailSent={(val) => {
                            setEmailSent(val);
                            if (!val) {
                                handleChangeAnimation(slideLeftToRightAnimation);
                            } else {
                                handleChangeAnimation(slideRightToLeftAnimation);
                            }
                        }}
                    />
                );
            case '3':
                return !isPhoneVerification ? (
                    <PhoneSection
                        onCodeSent={() => {
                            setIsPhoneVerification(true);
                            handleChangeAnimation(slideRightToLeftAnimation);
                        }}
                    />
                ) : (
                    <VerificationPhoneSection
                        onBack={() => {
                            handleChangeAnimation(slideLeftToRightAnimation);
                            setIsPhoneVerification(false);
                        }}
                    />
                );
            case '4':
                return <PasswordSection />;
            case '5':
                return <PersonalDetailsSection />;
            case '6':
                return <BillingSection />;
            case '7':
                return <CongratsSection />;
        }
    };

    const renderBusinessSections = () => {
        switch (currentStep?.id) {
            case '1':
                return <AccountTypeSection value={type} onChange={(val) => onChangeType(val)} />;
            case '2':
                return (
                    <EmailSection
                        isEmailSent={emailSent}
                        toggleIsEmailSent={(val) => setEmailSent(val)}
                    />
                );
            case '3':
                return !isPhoneVerification ? (
                    <PhoneSection onCodeSent={() => setIsPhoneVerification(true)} />
                ) : (
                    <VerificationPhoneSection onBack={() => setIsPhoneVerification(false)} />
                );
            case '4':
                return <PasswordSection />;
            case '5':
                return <PersonalDetailsSection />;
            case '6':
                return <BusinessDetailsSection />;
            case '7':
                return (
                    <BusinessAddressSection
                        type={addressStep}
                        onChange={(val) => setAddressStep(val)}
                    />
                );
            case '8':
                return <CongratsSection />;
        }
    };

    if (!isRender) return null;

    return (
        <motion.div {...animation} className={styles.wrapper}>
            <FormProvider {...formMethods}>
                {type === EUserType.INDIVIDUAL
                    ? renderPersonalSections()
                    : renderBusinessSections()}
            </FormProvider>
        </motion.div>
    );
};

export default SignUpLayout;
